import { Button } from "@mui/material";
import Form, { useForm } from "../../gui/Form/Form";
import { useOverlay } from "../../gui/Overlay/OverlayProvider";
import { usePath } from "../../libs/path/PathProvider";
import { Price } from "../../models/Price";
import PortUtils from "../../portlibs/PortUtils";
import DialogTitle from "../widgets/DialogTitle";
import { useRef, useState } from "react";
import FormControl from "../../gui/Form/FormControl";
import Validators from "../../gui/Form/Validators";
import TextInput, { Evaluators, Formatters, ITextInput } from "../../gui/TextInput/TextInput";
import Dropdown, { IDropdown } from "../../gui/Dropdown/Dropdown";
import { IToggleButton } from "../../gui/Button/ToggleButton";
import Checkbox from "../../gui/Button/Checkbox";
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker";
import { DividendFrequencies, TFrequency } from "../../shared/IPrice";
import SectorEditor, { ISectorEditor } from "./SectorEditor";
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider";
import PortAccount from "../../models/PortAccount";
import Prices from "../../models/Prices";

const DIALOG_ID = "config"
const Currencies = [{id:0,text:"CAD"},{id:1,text:"USD"}]
export default function ConfigDlg(props:{price:Price}) {
    const {price} = props
    const {popOverlay} = useOverlay()
    const [busy,setBusy] = useState<boolean>(false)
    const {valid} = useForm()
    const refName = useRef<ITextInput|null>(null)
    const refPrice = useRef<ITextInput|null>(null)
    const refPayout = useRef<ITextInput|null>(null)
    const refYield = useRef<ITextInput|null>(null)
    const refVariable = useRef<IToggleButton|null>(null)
    const refEx = useRef<ITextInput|null>(null)
    const refDiv = useRef<IDatePicker|null>(null)
    const refFreq = useRef<IDropdown<string>|null>(null)
    const refSector = useRef<ISectorEditor|null>(null)
    
    const {addSnackbar} = useSnackbar()
    const onSave = async () => {
        if (busy) return
        else setBusy(true)
        try {
            const p = price.clone()
            p.name = refName.current!.value as string
            p.price = Number(refPrice.current!.value) || 0
            p.payout = Number(refPayout.current!.value) || 0
            p.yield = Number(refYield.current!.value) || 0
            p.variableDividend = refVariable.current!.checked || false
            p.ex = Number(refEx.current!.value) || 0
            p.frequency = refFreq.current!.value as TFrequency
            p.sector = refSector.current!.sector
            p.sectors = refSector.current!.sectors || undefined
            await PortAccount.i.updatePrice(p)
            popOverlay(DIALOG_ID)
        }catch(e){
            addSnackbar(e)
        }finally {
            setBusy(false)
        }
    }
    return <>
        <DialogTitle title={props.price.id} overlayId={DIALOG_ID}/>
        <div className="content">
            <div className="grid gap-16">
                <FormControl validators={[Validators.Required("Name")]}>
                    <TextInput ref={refName} placeholder="Name" float={true} defaultValue={price.name}/>
                </FormControl>
                <FormControl validators={([Validators.PositiveNumber("Price")])}>
                    <TextInput ref={refPrice} placeholder="Price" float={true} defaultValue={price.price.toString()} evaluator={Evaluators.Number}/>
                </FormControl>
                <Dropdown options={Currencies} defaultValue={price.currency}/>
            </div>
            <div className="grid gap-16">
                <TextInput ref={refEx} placeholder="Ex (days)" float={true} defaultValue={price.ex.toString()} formatter={Formatters.PositiveRealNumber}/>
                <DatePicker ref={refDiv} placeholder="Dividend" float={true} defaultValue={price.date}/>
                <Dropdown ref={refFreq} options={DividendFrequencies} defaultValue={price.frequency||"q"}/>
            </div>
            <div className="grid gap-16">
                <TextInput ref={refPayout} placeholder="Payout" float={true} defaultValue={price.payout.toString()} evaluator={Evaluators.Number}/>
                <TextInput ref={refYield} placeholder="Yield" float={true} defaultValue={price.yield.toString()} evaluator={Evaluators.Number}/>
                <Checkbox ref={refVariable} defaultValue={price.variableDividend}>Variable Dividend</Checkbox>
            </div>
            <SectorEditor ref={refSector} price={price}/>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}

export function useConfigDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {hash, setHash} = usePath()
    const afterClosed = () => {
        const {holdingId} = PortUtils.parseHash(hash)
        setHash("/")
    }
    const openConfig = (holdingId:string) => {
        const price = Prices.i.get(holdingId)
        if (hasOverlay(DIALOG_ID) || !price) return
        addOverlay(DIALOG_ID, {
            children:<Form defaultValid={true}><ConfigDlg price={price}/></Form>,
            className:"dialog", afterClosed
        })
    }
    return {openConfig}
}